<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PilotFaqs from 'dashboard/api/pilot/faqs';
import Button from 'dashboard/components-next/button/Button.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import Confetti from 'dashboard/components-next/confetti/Confetti.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => Number(route.params.id));
const knowledgeBase = ref(null);

// 知识库切换弹窗
const selectKbDialogRef = ref(null);

// 图片路径
import faqThumbnail from 'assets/images/dashboard/pilot/faqs-light.svg';
import faqThumbnailDark from 'assets/images/dashboard/pilot/faqs-dark.svg';

// 图片加载错误处理
const handleThumbnailError = event => {
  event.target.style.display = 'none';
};

// 文件上传状态
const selectedFile = ref(null);
const fileInputRef = ref(null);
const isDragOver = ref(false);
const isSubmitting = ref(false);
const uploadProgress = ref(0);
const uploadResult = ref(null);
const errorFileUrl = ref(null);
const uploadStatusText = ref('');
const showSuccessAnimation = ref(false);
const showConfetti = ref(false);
const fileError = ref(''); // 文件级别错误

// 进度条组件引用
const progressBarRef = ref(null);

// 状态文字配置
const statusTexts = {
  parsing: '正在解析文件...',
  processing: '正在处理数据...',
  saving: '正在保存...',
  completing: '即将完成...',
};

// 假数据（模拟已上传文件）
const mockFiles = [
  { name: '常见问题汇总.xlsx', size: '156 KB', type: 'xlsx' },
  { name: '产品FAQ导入模板.xlsx', size: '89 KB', type: 'xlsx' },
  { name: '客服问答库.xlsx', size: '234 KB', type: 'xlsx' },
];

// 获取知识库详情
const fetchKnowledgeBase = async () => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
  }
};

// 监听路由参数变化，重新获取知识库数据
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchKnowledgeBase();
    }
  }
);

// 打开知识库选择弹窗
const openSelectKbDialog = () => {
  selectKbDialogRef.value?.open();
};

// 切换知识库
const handleKnowledgeBaseChange = newKbId => {
  router.replace({
    name: 'pilot_faqs_upload',
    params: { id: newKbId },
  });
};

// 文件上传相关函数
const openFileDialog = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = files => {
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const fileArray = Array.from(files);

  // 重置错误状态
  fileError.value = '';

  if (fileArray.length === 0) return;

  // 只允许一个文件
  if (fileArray.length > 1) {
    fileError.value = t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.FILE_LIMIT_ERROR');
    useAlert(fileError.value);
    return;
  }

  const file = fileArray[0];

  // 先选中文件，这样错误信息能显示在文件卡片下面
  selectedFile.value = file;
  uploadResult.value = null;
  errorFileUrl.value = null;

  // 检查文件类型
  const fileName = file.name.toLowerCase();
  const extension = fileName.split('.').pop();
  const allowedExtensions = ['xlsx', 'xls'];

  if (!allowedExtensions.includes(extension)) {
    fileError.value = t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.FILE_TYPE_ERROR');
    useAlert(fileError.value);
    return;
  }

  // 检查文件大小
  if (file.size > MAX_FILE_SIZE) {
    fileError.value = t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.FILE_SIZE_ERROR');
    useAlert(fileError.value);
  }
};

const handleFileChange = event => {
  const files = event.target.files;
  if (files.length === 0) return;
  handleFileSelect(files);
  event.target.value = ''; // 清空input，允许重复选择相同文件
};

const handleDragOver = event => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = event => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleDrop = event => {
  event.preventDefault();
  isDragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length === 0) return;
  handleFileSelect(files);
};

const removeFile = () => {
  selectedFile.value = null;
  uploadResult.value = null;
  errorFileUrl.value = null;
  fileError.value = '';
};

// 格式化文件大小
const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / k ** i).toFixed(1));
  return `${size} ${sizes[i]}`;
};

// 从文件名获取扩展名
const getFileExtension = file => {
  if (!file.name) return null;
  const match = file.name.toLowerCase().match(/\.(\w+)$/);
  if (!match) return null;
  return match[1];
};

// 返回列表页
const goBack = () => {
  router.push({
    name: 'pilot_knowledge_bases_faqs',
    params: { id: knowledgeBaseId.value },
  });
};

// 下载模板
const downloadTemplate = () => {
  // 模板文件路径
  const templatePath = '/assets/templates/faq_import_template.xlsx';
  const link = document.createElement('a');
  link.href = templatePath;
  link.download = 'faq_import_template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 提交处理
const handleSubmit = async () => {
  if (!selectedFile.value) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.NO_FILE_ERROR'));
    return;
  }
  // 前端验证失败，不发请求，不启动进度条
  if (fileError.value) {
    useAlert(fileError.value);
    return;
  }
  await submitFile();
};

// 更新状态文字
const updateStatusText = progress => {
  if (progress >= 90) {
    uploadStatusText.value = statusTexts.completing;
  } else if (progress < 30) {
    uploadStatusText.value = statusTexts.parsing;
  } else if (progress < 70) {
    uploadStatusText.value = statusTexts.processing;
  } else {
    uploadStatusText.value = statusTexts.saving;
  }
};

// 监听进度变化，更新状态文字
const handleProgressUpdate = val => {
  uploadProgress.value = val;
  updateStatusText(val);
};

const submitFile = async () => {
  isSubmitting.value = true;
  uploadProgress.value = 0;
  uploadResult.value = null;
  errorFileUrl.value = null;
  showSuccessAnimation.value = false;
  showConfetti.value = false;
  fileError.value = ''; // 重置错误状态
  uploadStatusText.value = statusTexts.parsing;

  // 启动进度条动画
  progressBarRef.value?.start();

  let pendingResult = null;

  try {
    const formData = new FormData();
    formData.append('file', selectedFile.value);
    formData.append('id', knowledgeBaseId.value);

    const response = await PilotFaqs.importExcel(formData);
    const result = response.data?.payload || {};

    const successCount = result.success_count || 0;
    const errorCount = result.error_count || 0;

    if (errorCount > 0) {
      // 部分失败
      pendingResult = {
        success: false,
        successCount,
        failedCount: errorCount,
        message: t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PARTIAL_SUCCESS', {
          successCount,
          failedCount: errorCount,
        }),
      };
      // 设置错误文件下载链接
      if (result.error_file_url) {
        errorFileUrl.value = result.error_file_url;
      }
    } else {
      // 全部成功
      pendingResult = {
        success: true,
        successCount,
        message: t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.SUCCESS_MESSAGE', {
          count: successCount,
        }),
      };
    }
  } catch (error) {
    console.error('Failed to import FAQs:', error);
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.ERROR_MESSAGE');
    // 文件级别错误
    fileError.value = errorMessage;
    pendingResult = null; // 不显示结果弹窗
  }

  // 不管成功还是失败，都等进度条走完
  await progressBarRef.value?.complete();
  isSubmitting.value = false;

  // 根据结果显示不同效果
  if (fileError.value) {
    // 文件级别错误，显示 Toast
    useAlert(fileError.value);
  } else if (pendingResult?.success) {
    // 全部成功
    showSuccessAnimation.value = true;
    showConfetti.value = true;
    uploadResult.value = pendingResult;
  } else if (pendingResult?.success === false) {
    // 部分失败
    uploadResult.value = pendingResult;
    useAlert(t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PARTIAL_ERROR_TOAST'));
  }
};

// 确认成功，返回列表
const handleConfirm = () => {
  router.push({
    name: 'pilot_knowledge_bases_faqs',
    params: { id: knowledgeBaseId.value },
  });
};

// 下载错误文件
const downloadErrorFile = () => {
  if (errorFileUrl.value) {
    const link = document.createElement('a');
    link.href = errorFileUrl.value;
    link.download = 'faq_import_errors.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

onMounted(() => {
  fetchKnowledgeBase();
  window.addEventListener('beforeunload', handleBeforeUnload);
});

// 离开确认：只有上传中才需要确认
const hasUnsavedChanges = computed(() => {
  return isSubmitting.value;
});

const handleBeforeUnload = event => {
  if (hasUnsavedChanges.value) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
};

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    const confirmed = window.confirm(
      t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.LEAVE_CONFIRM')
    );
    if (confirmed) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- 彩带庆祝效果 -->
    <Confetti :visible="showConfetti" />

    <!-- 成功确认弹窗 -->
    <Teleport to="body">
      <div v-if="uploadResult?.success" class="success-modal-overlay">
        <div class="success-modal-content">
          <div class="flex flex-col items-center gap-4">
            <div
              class="w-16 h-16 rounded-full bg-n-emerald-100 dark:bg-n-emerald-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-check text-3xl text-n-emerald-600 dark:text-n-emerald-400"
              />
            </div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.SUCCESS_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 text-center">
              {{ uploadResult.message }}
            </p>
            <Button
              :label="t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.CONFIRM_BUTTON')"
              blue
              class="w-full"
              @click="handleConfirm"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center h-20">
          <button
            type="button"
            class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
            @click="goBack"
          >
            <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
          </button>
          <h1
            class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
          >
            {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.TITLE') }}
          </h1>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6">
        <div class="flex flex-col gap-6">
          <!-- 虚线介绍区域 -->
          <section class="custom-dashed-border rounded-2xl py-5 px-6">
            <div
              class="flex flex-col md:flex-row items-start md:items-center gap-6"
            >
              <div
                class="flex-shrink-0 w-[7.5rem] h-[6.5rem] rounded-lg flex items-center justify-center overflow-hidden"
              >
                <img
                  :src="faqThumbnail"
                  :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
                  class="w-full h-full object-cover rounded-lg block dark:hidden"
                  @error="handleThumbnailError"
                />
                <img
                  :src="faqThumbnailDark"
                  :alt="t('PILOT.KNOWLEDGE_BASES.FAQS.ADD.GUIDE_TITLE')"
                  class="w-full h-full object-cover rounded-lg hidden dark:block"
                  @error="handleThumbnailError"
                />
              </div>
              <div class="flex flex-col flex-1 gap-3">
                <p class="text-n-slate-12 text-sm font-medium">
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.GUIDE_TITLE') }}
                </p>
                <p class="text-n-slate-11 text-sm">
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.GUIDE_DESCRIPTION') }}
                </p>
              </div>
            </div>
          </section>

          <!-- 知识库信息（锁定状态） -->
          <div
            class="w-full flex items-start justify-between gap-3 p-4 rounded-xl outline-1 outline outline-n-container bg-n-solid-2 text-left"
          >
            <!-- Logo -->
            <div
              class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
              :class="knowledgeBase?.logo_url ? '' : 'bg-n-brand/10'"
            >
              <img
                v-if="knowledgeBase?.logo_url"
                :src="knowledgeBase.logo_url"
                :alt="knowledgeBase?.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-base font-semibold text-n-brand">
                {{ knowledgeBase?.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
            </div>

            <!-- 信息 -->
            <div class="flex flex-col min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 font-medium">{{
                  knowledgeBase?.name || '加载中...'
                }}</span>
                <span
                  v-if="knowledgeBase?.knowledge_base_type === 'faq'"
                  class="px-2 py-0.5 text-xs rounded-full font-medium bg-n-emerald-100 text-n-emerald-700 dark:bg-n-emerald-900/30 dark:text-n-emerald-400"
                >
                  FAQs
                </span>
              </div>
              <span class="text-sm text-n-slate-11 mt-2">
                {{
                  knowledgeBase?.description ||
                  t('PILOT.KNOWLEDGE_BASES.SELECT_KB_DIALOG.NO_DESCRIPTION')
                }}
              </span>
              <!-- 文档和FAQ数量 -->
              <div class="flex items-center gap-4 text-sm text-n-slate-11 mt-2">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-file-text size-3" />
                  <span>{{ knowledgeBase?.documents_count || 0 }} 文档</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-message-circle-question size-3" />
                  <span>{{ knowledgeBase?.faqs_count || 0 }} FAQ</span>
                </div>
              </div>
            </div>

            <!-- 切换按钮 -->
            <button
              type="button"
              class="flex items-center gap-1 text-sm text-n-slate-9 hover:text-n-brand flex-shrink-0 transition-colors"
              @click="openSelectKbDialog"
            >
              <span class="i-lucide-arrow-right-left size-4" />
              <span>{{ t('PILOT.KNOWLEDGE_BASES.SWITCH_KB') }}</span>
            </button>
          </div>

          <!-- 拖拽上传区域 -->
          <div
            class="relative flex flex-col items-center justify-center h-64 p-6 border-2 border-dashed rounded-lg transition-colors"
            :class="[
              isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              isDragOver
                ? 'border-n-blue-5 bg-n-blue-1 dark:bg-n-blue-2'
                : 'border-n-slate-3 hover:border-n-slate-4',
            ]"
            @dragover="isSubmitting ? null : handleDragOver($event)"
            @dragleave="isSubmitting ? null : handleDragLeave($event)"
            @drop="isSubmitting ? null : handleDrop($event)"
            @click="isSubmitting ? null : openFileDialog()"
          >
            <input
              ref="fileInputRef"
              type="file"
              accept=".xlsx,.xls"
              class="hidden"
              @change="handleFileChange"
            />
            <div class="i-lucide-upload-cloud text-5xl text-n-slate-7 mb-4" />
            <p class="text-base font-medium text-n-slate-12 mb-2">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.DRAG_DROP_TITLE') }}
            </p>
            <p class="text-sm text-n-slate-11 mb-4">
              {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.DRAG_DROP_HELP') }}
            </p>
            <!-- 下载模板按钮 -->
            <Button
              :label="t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.DOWNLOAD_TEMPLATE')"
              icon="i-lucide-download"
              variant="faded"
              color="slate"
              size="sm"
              @click.stop="downloadTemplate"
            />
          </div>

          <!-- 已选文件 -->
          <div v-if="selectedFile" class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.SELECTED_FILE') }}
              </h3>
            </div>

            <div
              class="flex items-start justify-between p-3 bg-n-slate-1 dark:bg-n-slate-7 rounded-lg group"
            >
              <div class="flex items-start gap-3 flex-1 min-w-0">
                <FileIcon
                  v-if="getFileExtension(selectedFile)"
                  :file-type="getFileExtension(selectedFile)"
                  class="text-2xl text-n-slate-7 flex-shrink-0"
                />
                <div
                  v-else
                  class="i-lucide-file text-2xl text-n-slate-7 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ selectedFile.name }}
                    </p>
                    <!-- 成功状态图标 -->
                    <span
                      v-if="showSuccessAnimation"
                      class="i-lucide-check text-green-500 flex-shrink-0"
                    />
                  </div>
                  <p class="text-xs text-n-slate-11">
                    {{ formatFileSize(selectedFile.size) }}
                  </p>
                  <!-- 进度条组件 -->
                  <ProgressBar
                    ref="progressBarRef"
                    :visible="isSubmitting"
                    :status-text="uploadStatusText"
                    :show-success="showSuccessAnimation"
                    @update:progress="handleProgressUpdate"
                  />
                  <!-- 文件级别错误 -->
                  <p
                    v-if="fileError"
                    class="text-xs text-n-red-11 mt-2 flex items-center gap-1.5"
                  >
                    <span class="i-lucide-alert-circle text-sm" />
                    {{ fileError }}
                  </p>
                </div>
              </div>
              <Button
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :disabled="isSubmitting"
                class="ml-3 flex-shrink-0"
                @click="removeFile"
              />
            </div>
          </div>

          <!-- 上传结果：部分失败 -->
          <div v-if="uploadResult && !uploadResult.success">
            <div
              class="p-4 rounded-lg bg-gradient-to-r from-n-amber-3 to-n-amber-2 dark:from-n-amber-2/30 dark:to-n-amber-2/10"
            >
              <div class="flex items-start gap-3">
                <div
                  class="flex-shrink-0 w-9 h-9 rounded-full bg-n-amber-4 dark:bg-n-amber-5/50 flex items-center justify-center"
                >
                  <span
                    class="i-lucide-alert-triangle text-lg text-n-amber-11 dark:text-n-amber-10"
                  />
                </div>
                <div class="flex-1">
                  <h4
                    class="text-sm font-medium text-n-slate-12 dark:text-n-slate-1 mb-0.5"
                  >
                    {{
                      t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PARTIAL_ERROR_TITLE')
                    }}
                  </h4>
                  <p class="text-sm text-n-slate-11 dark:text-n-slate-4 mb-3">
                    {{
                      t(
                        'PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PARTIAL_ERROR_DESC',
                        { count: uploadResult.failedCount }
                      )
                    }}
                  </p>
                  <Button
                    :label="
                      t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.DOWNLOAD_ERRORS')
                    "
                    icon="i-lucide-download"
                    variant="faded"
                    color="slate"
                    size="sm"
                    @click="downloadErrorFile"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 假数据预览区域（仅当没有选择文件时显示） -->
          <div
            v-if="!selectedFile && !uploadResult"
            class="relative overflow-hidden rounded-xl"
          >
            <!-- 假数据文件列表（半透明） -->
            <div class="w-full space-y-2 opacity-50 pointer-events-none">
              <div
                v-for="(mockFile, index) in mockFiles"
                :key="index"
                class="flex items-center p-3 bg-n-slate-1 dark:bg-n-slate-7 rounded-lg"
              >
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <FileIcon
                    :file-type="mockFile.type"
                    class="text-2xl text-n-slate-7 flex-shrink-0"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ mockFile.name }}
                    </p>
                    <p class="text-xs text-n-slate-11">
                      {{ mockFile.size }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 渐变蒙层 + 标题/描述/按钮 -->
            <div
              class="absolute inset-x-0 top-0 flex flex-col items-center justify-center w-full h-full bg-gradient-to-b from-transparent from-0% via-white/50 dark:via-n-slate-800/50 to-white dark:to-n-slate-800"
            >
              <div class="flex flex-col items-center justify-center gap-3">
                <h3 class="text-3xl font-medium text-center text-n-slate-12">
                  {{ t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PREVIEW_TITLE') }}
                </h3>
                <p class="max-w-xl text-base text-center text-n-slate-11">
                  {{
                    t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.PREVIEW_DESCRIPTION')
                  }}
                </p>
                <Button
                  :label="t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.START_UPLOAD')"
                  icon="i-lucide-upload-cloud"
                  blue
                  :disabled="isSubmitting"
                  @click="openFileDialog"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer
      class="sticky bottom-0 z-10 px-6 py-4 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
    >
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex gap-3 justify-end">
          <Button
            :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.CANCEL')"
            variant="faded"
            color="slate"
            @click="goBack"
          />
          <Button
            :label="
              t('PILOT.KNOWLEDGE_BASES.FAQS.IMPORT.UPLOAD_BUTTON', {
                count: selectedFile ? 1 : 0,
              })
            "
            :is-loading="isSubmitting"
            :disabled="isSubmitting || !selectedFile"
            blue
            @click="handleSubmit"
          />
        </div>
      </div>
    </footer>
  </div>

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    action-type="switch"
    @select="handleKnowledgeBaseChange"
  />
</template>

<style scoped>
/* 成功弹窗样式 */
.success-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.success-modal-content {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  max-width: 28rem;
  margin: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.dark .success-modal-content {
  background-color: #1e293b;
}
</style>
